// Cart totals (cents) for progress bars, dynamic pricing and the cart page. No /cart.js read until a feature calls load().
// Blocks can load this before the layout's own tag runs; a second copy must leave the first one in place.
window.CartData ||= (() => {

  // Mirrored by snippets/dynamic-price-template.liquid.
  const STORAGE_KEY = 'ja-cart-data:v1';
  const listeners = new Set();
  let state = null;
  let readId = 0;
  let loading = null;
  let reading = null;
  let settle = null;

  const cartToken = () => decodeURIComponent(document.cookie.match(/(?:^|;\s*)cart=([^;]*)/)?.[1] || '');

  const fromAjax = (cart) => ({
    subtotal: cart.items_subtotal_price,
    total: cart.total_price,
    itemCount: cart.item_count,
    currency: cart.currency,
    discountTitles: [...new Set([
      ...cart.cart_level_discount_applications,
      ...cart.items.flatMap(item => item.line_level_discount_allocations.map(allocation => allocation.discount_application))
    ].map(application => application.title))],
    items: cart.items.map(item => ({ productId: item.product_id, title: item.product_title, quantity: item.quantity }))
  });

  const readStored = () => {
    try {
      const { token, savedAt, subtotal, total, itemCount, currency } = JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
      if ( token === cartToken() && Date.now() - savedAt < 30 * 60 * 1000 && Number.isFinite(total) ) {
        return { subtotal, total, itemCount, currency, discountTitles: [], items: [], provisional: true };
      }
    } catch {}
    return null;
  };

  const set = (next) => {
    if ( JSON.stringify(state) === JSON.stringify(next) ) return;
    state = next;
    if ( ! state.provisional ) {
      const { subtotal, total, itemCount, currency } = state;
      // sessionStorage, not a cookie: a cookie would grow every request's headers.
      try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ token: cartToken(), savedAt: Date.now(), subtotal, total, itemCount, currency })); } catch {}
    }
    // A feature's bug must never fail the cart action that called update().
    listeners.forEach(fn => {
      try { fn(state); } catch (error) { console.error('cart-data', error); }
    });
  };

  const refresh = (reason = 'manual', priority = 'auto') => {
    const id = ++readId;
    return fetch(`${KROWN.settings.routes.cart_url}.js?ja-cart-data=${reason}`, { priority })
      .then(response => response.json())
      .then(cart => { if ( id === readId ) set(fromAjax(cart)); })
      .catch(error => console.warn('cart-data', error));
  };

  // Theme changes call this after dispatching their event, which makes that event's re-read below stand down.
  const update = (cart) => {
    ++readId;
    set(fromAjax(cart));
  };

  // App changes re-read the cart; only the newest of a burst counts. Notes and attributes never change totals.
  const onCartEvent = (event) => {
    if ( ! loading ) return;
    const id = ++readId;
    Promise.resolve(event.promise)
      .then(({ userErrors } = {}) => { if ( id === readId && ! userErrors?.length ) refresh(event.context || 'event'); })
      .catch(() => { if ( id === readId ) refresh('event-error'); });
  };
  document.addEventListener('shopify:cart:lines-update', onCartEvent);
  document.addEventListener('shopify:cart:discount-update', onCartEvent);
  window.addEventListener('pageshow', (event) => { if ( event.persisted && loading ) refresh('pageshow'); });

  const read = (priority) => {
    reading ||= refresh('load', priority);
    settle?.(reading);
  };
  const idle = window.requestIdleCallback ? (fn) => requestIdleCallback(fn, { timeout: 2000 }) : (fn) => setTimeout(fn, 200);

  // 'idle' reads once the browser is idle; 'high' reads now, even with an idle read pending. Settles after the first read.
  const load = (priority = 'idle') => {
    if ( ! loading ) {
      if ( state && ! state.provisional ) return loading = Promise.resolve();
      const stored = readStored();
      if ( stored ) set(stored);
      loading = stored ? Promise.resolve() : new Promise(resolve => { settle = resolve; });
      if ( priority !== 'high' ) idle(() => read('low'));
    }
    if ( priority === 'high' ) read('high');
    return loading;
  };

  return {
    get: () => state,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    load,
    refresh,
    update
  };

})();

// Readiness signal for scripts that load first: customElements.whenDefined('cart-data').
customElements.get('cart-data') || customElements.define('cart-data', class extends HTMLElement {});
